"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useUnsavedChanges } from "@/app/contexts/UnsavedChangesContext";

export function NavigationGuard() {
  const pathname = usePathname();
  const { hasUnsavedChanges, attemptNavigation } = useUnsavedChanges();
  const isBlockingRef = useRef(false);

  useEffect(() => {
    console.log(`[NavigationGuard] hasUnsavedChanges: ${hasUnsavedChanges}`);

    // Handle browser back/forward buttons
    const handlePopState = (event: PopStateEvent) => {
      console.log(
        `[NavigationGuard] PopState event with hasUnsavedChanges: ${hasUnsavedChanges}`
      );
      if (hasUnsavedChanges && !isBlockingRef.current) {
        console.log(`[NavigationGuard] Blocking popstate navigation`);
        isBlockingRef.current = true;
        // Prevent the navigation
        event.preventDefault();
        // Push the current state back to prevent navigation
        window.history.pushState(null, "", pathname);
        attemptNavigation(window.location.href, "push");
        setTimeout(() => {
          isBlockingRef.current = false;
        }, 100);
      }
    };

    // Handle link clicks
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest("a[href]") as HTMLAnchorElement;

      if (link && hasUnsavedChanges && !isBlockingRef.current) {
        const href = link.getAttribute("href");
        if (
          href &&
          !href.startsWith("#") &&
          !href.startsWith("mailto:") &&
          !href.startsWith("tel:") &&
          !href.startsWith("javascript:") &&
          !href.startsWith("data:")
        ) {
          console.log(`[NavigationGuard] Blocking link click to: ${href}`);
          isBlockingRef.current = true;
          event.preventDefault();
          event.stopPropagation();
          attemptNavigation(href, "push");
          setTimeout(() => {
            isBlockingRef.current = false;
          }, 100);
        }
      }
    };

    // Handle form submissions that might navigate
    const handleFormSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      if (form && hasUnsavedChanges && !isBlockingRef.current) {
        const action = form.getAttribute("action");
        if (action && action !== window.location.pathname) {
          console.log(
            `[NavigationGuard] Blocking form submission to: ${action}`
          );
          isBlockingRef.current = true;
          event.preventDefault();
          attemptNavigation(action, "push");
          setTimeout(() => {
            isBlockingRef.current = false;
          }, 100);
        }
      }
    };

    // Handle programmatic navigation attempts
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    // Add event listeners with null checks
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", handlePopState);
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("click", handleLinkClick, true);
      document.addEventListener("submit", handleFormSubmit, true);
    }

    // Cleanup
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("popstate", handlePopState);
        window.removeEventListener("beforeunload", handleBeforeUnload);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("click", handleLinkClick, true);
        document.removeEventListener("submit", handleFormSubmit, true);
      }
    };
  }, [pathname, hasUnsavedChanges, attemptNavigation]);

  return null; // This component doesn't render anything
}
